import { IAvatar, IAvatarBody, IAvatarPart, IStudent, IStudentItem, IStudentParams, ITableRes } from 'types'

import { baseApi } from './baseApi'
import { MAX_PER_PAGE } from 'utils/constants'

export const studentsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    GET_students: builder.query<ITableRes<IStudentItem>, IStudentParams>({
      query: (params) => ({
        url: '/students/',
        params,
      }),
      providesTags: ['students'],
    }),
    GET_studentByProfileId: builder.query<any, string>({
      query: (id) => {
        return {
          url: `/students/profile/${id}/`,
        }
      },
      providesTags: ['student'],
      transformResponse: (response: any): any => {
        const student_profile_id = response.student_profile.id
        const academic_id = response.student_profile.academic_id
        const grade_level = response.student_profile.grade_level
        const comment = response.student_profile.comment
        const avatar = response.student_profile.avatar

        delete response.student_profile
        return {
          ...response,
          student_profile_id,
          academic_id,
          grade_level,
          comment,
          avatar
        }
      },
    }),
    GET_studentById: builder.query<IStudent, string>({
      query: (id) => {
        return {
          url: `/students/${id}/`,
        }
      },
      providesTags: ['student'],
      transformResponse: (response: IStudent): any => {
        const student_profile_id = response.student_profile.id
        const academic_id = response.student_profile.academic_id
        const grade_level = response.student_profile.grade_level
        const comment = response.student_profile.comment
        const avatar = response.student_profile.avatar

        delete response.student_profile
        return {
          ...response,
          student_profile_id,
          academic_id,
          grade_level,
          comment,
          avatar
        }
      },
    }),
    POST_student: builder.mutation<IStudent, IStudent>({
      query: (body) => ({
        url: `/students/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['students','avatar'],
    }),
    PUT_student: builder.mutation<IStudent, { id: string; body: IStudent }>({
      query: ({ id, body }) => ({
        url: `/students/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['students', 'student', 'avatar'],
    }),
    DELETE_student: builder.mutation<IStudent, string>({
      query: (id) => ({
        url: `/students/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['students'],
    }),
    GET_studentsProfileSelect: builder.query<any, IStudentParams>({
      query: (params) => ({
        url: `/students/profile/select/`,
        params: {...params, page_size: MAX_PER_PAGE},
      }),
      providesTags: ['students-profile-select'],
      transformResponse: (response: any) => {
        const hasMore = Boolean(response?.next)
        const count = response?.count || 0
        const list = response?.results || []
        return {
          list,
          count,
          hasMore,
        }
      },
    }),
    GET_avatarParts: builder.query<ITableRes<IAvatarPart>, any>({
      query: (params) => ({
        url: '/avatar-parts/',
        params
      }),
      providesTags: ['avatar-parts'],
    }),
    GET_studentAvatar: builder.query<IAvatar, void>({
      query: () => ({
        url: '/student-avatar/'
      }),
      providesTags: ['avatar'],
    }),
    POST_studentAvatar: builder.mutation<IAvatar, any>({
      query: (formData) => ({
        url: `/student-avatar/`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['avatar', 'user', 'student', 'students'],
    }),
    GET_studentInfoQuestions: builder.query<any, void>({
      query: () => ({
        url: '/student-info-questions/'
      }),
      providesTags: ['student-questions'],
    }),
    POST_studentInfo: builder.mutation<IAvatar, IAvatarBody>({
      query: (body) => ({
        url: `/student-info/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['student-questions'],
    }),
    GET_studentsProfileCounters: builder.query<any, void>({
      query: () => ({
        url: `/student-welcome-screen-counters/`
      }),
      providesTags: ['student-counters']
    }),
    GET_studentLearningStyleQuestions: builder.query<any, void>({
      query: () => ({
        url: `/my-learning-style/`
      }),
      providesTags: ['my-learning-style']
    }),
    POST_studentLearningStyle: builder.mutation<any, any>({
      query: (body) => ({
        url: `/my-learning-style/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['my-learning-style'],
    }),
    GET_learningStyleQuestions: builder.query<any, any>({
      query: (id) => ({
        url: `/learning-style/${id}/`
      }),
      providesTags: ['learning-style']
    }),
    PUT_resetLearningStyle: builder.mutation<any, string>({
      query: (id) => ({
        url: `/learning-style/restart/${id}/`,
        method: 'PUT',
      }),
      invalidatesTags: ['learning-style'],
    }),
  }),
})

export const {
  useGET_studentsQuery,
  useGET_studentByIdQuery,
  useGET_studentByProfileIdQuery,
  usePOST_studentMutation,
  usePUT_studentMutation,
  useDELETE_studentMutation,
  useGET_studentsProfileSelectQuery,
  useGET_avatarPartsQuery,
  useGET_studentAvatarQuery,
  usePOST_studentAvatarMutation,
  useGET_studentInfoQuestionsQuery,
  usePOST_studentInfoMutation,
  useGET_studentsProfileCountersQuery,
  useGET_learningStyleQuestionsQuery,
  useGET_studentLearningStyleQuestionsQuery,
  usePOST_studentLearningStyleMutation,
  usePUT_resetLearningStyleMutation,
} = studentsAPI
